import { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, FormControl, IconButton, MenuItem, Select, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import Panel from '../reusable/Panel';
import AddIcon from '../assets/eoa-configuration-add.svg';
import useUserDetails from '../hooks/useUserDetails';
import { getUsers } from '../../services/userManagementService';
import { SurveyLanguageOption } from '../../services/eoaConfigServices';
import { UserListItem } from '../../interfaces/UserManagementInterface';
import { digitsOnlyError } from './fieldValidation';
import {
  ENGLISH_LANGUAGE,
  EoaaInfoForm,
  PersonRow,
  academicYearOptions,
  additionalLanguagesLabel,
  additionalSurveyLanguages,
  addPerson,
  addStaffRole,
  duplicatePersonIds,
  filterSuggestedUsers,
  normalizeExistingPeople,
  personFieldError,
  selectedAdditionalLanguageIds,
  removePerson,
  removeStaffRole,
  setDigitField,
  setPerson,
  setPersonField,
  updateStaffRole,
} from './eoaaInfoForm';

interface EoaaInfoFieldsProps {
  value: EoaaInfoForm;
  onChange: (value: EoaaInfoForm) => void;
  languages: SurveyLanguageOption[];
}

const USER_SUGGESTION_PAGE_SIZE = 25;
const USER_SEARCH_DELAY_MS = 300;

interface AssigneeOption {
  id: string;
  name: string;
  email: string;
}

const AssigneeField = ({
  person,
  users,
  assignedUserIds,
  duplicatePersonIds,
  showDuplicateMessage,
  field,
  onPersonSelect,
  onPersonFieldChange,
  onQueryChange,
}: {
  person: PersonRow;
  users: AssigneeOption[];
  assignedUserIds: ReadonlySet<string>;
  duplicatePersonIds: ReadonlySet<string>;
  showDuplicateMessage: boolean;
  field: 'name' | 'email';
  onPersonSelect: (id: string, person: AssigneeOption | null) => void;
  onPersonFieldChange: (id: string, field: 'name' | 'email', value: string) => void;
  onQueryChange: (query: string) => void;
}) => {
  const selected = person.selectedUserId ? (users.find((user) => user.id === person.selectedUserId) ?? null) : null;
  const isDuplicate = duplicatePersonIds.has(person.id);
  const error = personFieldError(person, field, isDuplicate);

  return (
    <Autocomplete
      className="eoa-configuration-person-input"
      autoHighlight
      freeSolo
      includeInputInList
      openOnFocus
      options={users}
      value={selected}
      inputValue={person[field]}
      filterOptions={(suggested, { inputValue }) =>
        filterSuggestedUsers(suggested, inputValue).filter(
          (user) => user.id === person.selectedUserId || !assignedUserIds.has(user.id)
        )
      }
      getOptionLabel={(option) => (typeof option === 'string' ? option : option[field])}
      isOptionEqualToValue={(option, optionValue) =>
        typeof option !== 'string' &&
        typeof optionValue !== 'string' &&
        option.email.toLowerCase() === optionValue.email.toLowerCase()
      }
      onOpen={() => onQueryChange('')}
      onInputChange={(_event, next, reason) => {
        if (reason === 'input') {
          onPersonFieldChange(person.id, field, next);
          onQueryChange(next);
        }
        if (reason === 'clear') {
          onPersonFieldChange(person.id, field, '');
          onQueryChange('');
        }
        if (reason === 'reset') onQueryChange('');
      }}
      onChange={(_event, option) => {
        if (option && typeof option !== 'string') onPersonSelect(person.id, option);
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {option.name} ({option.email})
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={field === 'name' ? 'Name' : 'Email'}
          inputProps={params.inputProps}
          error={Boolean(error)}
          helperText={isDuplicate && !showDuplicateMessage ? undefined : error}
        />
      )}
    />
  );
};

const PersonRows = ({
  people,
  users,
  assignedUserIds,
  duplicatePersonIds,
  duplicateMessagePersonId,
  onPersonSelect,
  onPersonFieldChange,
  onQueryChange,
  onAdd,
  onRemove,
}: {
  people: PersonRow[];
  users: AssigneeOption[];
  assignedUserIds: ReadonlySet<string>;
  duplicatePersonIds: ReadonlySet<string>;
  duplicateMessagePersonId: string | null;
  onPersonSelect: (id: string, person: AssigneeOption | null) => void;
  onPersonFieldChange: (id: string, field: 'name' | 'email', value: string) => void;
  onQueryChange: (query: string) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
}) => (
  <div className="eoa-configuration-removable-list">
    {people.map((person) => (
      <div key={person.id} className="eoa-configuration-person-row-group">
        <div className="eoa-configuration-person-row">
          <AssigneeField
            person={person}
            users={users}
            assignedUserIds={assignedUserIds}
            duplicatePersonIds={duplicatePersonIds}
            showDuplicateMessage={false}
            field="name"
            onPersonSelect={onPersonSelect}
            onPersonFieldChange={onPersonFieldChange}
            onQueryChange={onQueryChange}
          />
          <AssigneeField
            person={person}
            users={users}
            assignedUserIds={assignedUserIds}
            duplicatePersonIds={duplicatePersonIds}
            showDuplicateMessage={duplicateMessagePersonId === person.id}
            field="email"
            onPersonSelect={onPersonSelect}
            onPersonFieldChange={onPersonFieldChange}
            onQueryChange={onQueryChange}
          />
          {people.length > 1 && (
            <IconButton
              className="eoa-configuration-remove-button"
              aria-label="Remove person"
              onClick={() => onRemove(person.id)}
            >
              <CloseIcon />
            </IconButton>
          )}
        </div>
      </div>
    ))}
    <button type="button" className="eoa-configuration-add-link" onClick={onAdd}>
      <img src={AddIcon} alt="" width={18} height={18} />
      Add more
    </button>
  </div>
);

const EoaaInfoFields = ({ value, onChange, languages }: EoaaInfoFieldsProps) => {
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [userQuery, setUserQuery] = useState('');
  const [lastEditedPersonId, setLastEditedPersonId] = useState<string | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const yearOptions = useMemo(() => academicYearOptions(), []);
  const assignedUserIdsFor = (people: PersonRow[]) =>
    new Set(people.map((person) => person.selectedUserId).filter((id): id is string => Boolean(id)));
  const districtDataLeadUserIds = useMemo(() => assignedUserIdsFor(value.districtDataLeads), [value.districtDataLeads]);
  const leadFacilitatorUserIds = useMemo(() => assignedUserIdsFor(value.leadFacilitators), [value.leadFacilitators]);
  const facilitatorUserIds = useMemo(() => assignedUserIdsFor(value.facilitators), [value.facilitators]);
  const duplicatePeople = useMemo(() => duplicatePersonIds(value), [value]);
  const workshopDate = value.workshopDate ? dayjs(value.workshopDate) : null;

  useEffect(() => {
    if (!userDetails?.access_token) return;

    const timeoutId = window.setTimeout(() => {
      const loadUsers = async () => {
        try {
          const response = await getUsers(userDetails.access_token, {
            search: userQuery.trim() || undefined,
            size: USER_SUGGESTION_PAGE_SIZE,
          });
          setUsers(response.data.items);
        } catch {
          setUsers([]);
        }
      };

      void loadUsers();
    }, USER_SEARCH_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [userDetails?.access_token, userQuery]);

  useEffect(() => {
    const normalized = normalizeExistingPeople(valueRef.current, users);
    if (normalized !== valueRef.current) onChangeRef.current(normalized);
  }, [users]);

  const setWorkshopDate = (date: Dayjs | null) => {
    onChange({ ...value, workshopDate: date?.isValid() ? date.format('YYYY-MM-DD') : '' });
  };

  return (
    <>
      <Panel className="eoa-configuration-card">
        <h3 className="eoa-configuration-card-title">EOAA information: General</h3>
        <div className="eoa-configuration-fields">
          <div className="eoa-configuration-field eoa-configuration-field--year">
            <span className="eoa-configuration-field-label">Graduating year of EOAA cohort</span>
            <FormControl className="eoa-configuration-year-select">
              <Select
                displayEmpty
                value={value.yearEnding}
                onChange={(event) => onChange({ ...value, yearEnding: event.target.value })}
                renderValue={(selected) => yearOptions.find((option) => option.value === selected)?.label ?? 'Year'}
              >
                {yearOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className="eoa-configuration-field eoa-configuration-field--date">
            <span className="eoa-configuration-field-label">Date of student journey workshop</span>
            <DatePicker
              value={workshopDate?.isValid() ? workshopDate : null}
              onChange={setWorkshopDate}
              format="MM/DD/YYYY"
              slotProps={{ textField: { placeholder: 'MM/DD/YYYY' } }}
            />
          </div>

          <div className="eoa-configuration-field eoa-configuration-field--term-count">
            <span className="eoa-configuration-field-label">Minimum cell size for PII suppression</span>
            <TextField
              placeholder="Number"
              value={value.minCellSize}
              error={Boolean(digitsOnlyError(value.minCellSize))}
              helperText={digitsOnlyError(value.minCellSize)}
              onChange={(event) => onChange(setDigitField(value, 'minCellSize', event.target.value))}
              inputProps={{ inputMode: 'numeric' }}
            />
          </div>

          <div className="eoa-configuration-field">
            <span className="eoa-configuration-field-label">District data leads for this EOAA</span>
            <PersonRows
              people={value.districtDataLeads}
              users={users}
              assignedUserIds={districtDataLeadUserIds}
              duplicatePersonIds={duplicatePeople}
              duplicateMessagePersonId={lastEditedPersonId}
              onPersonSelect={(id, person) =>
                onChange(
                  setPerson(
                    value,
                    'districtDataLeads',
                    id,
                    person ? { ...person, selectedUserId: person.id } : { name: '', email: '' }
                  )
                )
              }
              onPersonFieldChange={(id, field, next) => {
                setLastEditedPersonId(id);
                onChange(setPersonField(value, 'districtDataLeads', id, field, next));
              }}
              onQueryChange={setUserQuery}
              onAdd={() => onChange(addPerson(value, 'districtDataLeads'))}
              onRemove={(id) => onChange(removePerson(value, 'districtDataLeads', id))}
            />
          </div>

          <div className="eoa-configuration-field">
            <span className="eoa-configuration-field-label">Lead facilitator(s) for this EOAA</span>
            <PersonRows
              people={value.leadFacilitators}
              users={users}
              assignedUserIds={leadFacilitatorUserIds}
              duplicatePersonIds={duplicatePeople}
              duplicateMessagePersonId={lastEditedPersonId}
              onPersonSelect={(id, person) =>
                onChange(
                  setPerson(
                    value,
                    'leadFacilitators',
                    id,
                    person ? { ...person, selectedUserId: person.id } : { name: '', email: '' }
                  )
                )
              }
              onPersonFieldChange={(id, field, next) => {
                setLastEditedPersonId(id);
                onChange(setPersonField(value, 'leadFacilitators', id, field, next));
              }}
              onQueryChange={setUserQuery}
              onAdd={() => onChange(addPerson(value, 'leadFacilitators'))}
              onRemove={(id) => onChange(removePerson(value, 'leadFacilitators', id))}
            />
          </div>

          <div className="eoa-configuration-field">
            <span className="eoa-configuration-field-label">Facilitator(s) for this EOAA</span>
            <PersonRows
              people={value.facilitators}
              users={users}
              assignedUserIds={facilitatorUserIds}
              duplicatePersonIds={duplicatePeople}
              duplicateMessagePersonId={lastEditedPersonId}
              onPersonSelect={(id, person) =>
                onChange(
                  setPerson(
                    value,
                    'facilitators',
                    id,
                    person ? { ...person, selectedUserId: person.id } : { name: '', email: '' }
                  )
                )
              }
              onPersonFieldChange={(id, field, next) => {
                setLastEditedPersonId(id);
                onChange(setPersonField(value, 'facilitators', id, field, next));
              }}
              onQueryChange={setUserQuery}
              onAdd={() => onChange(addPerson(value, 'facilitators'))}
              onRemove={(id) => onChange(removePerson(value, 'facilitators', id))}
            />
          </div>
        </div>
      </Panel>

      <Panel className="eoa-configuration-card">
        <h3 className="eoa-configuration-card-title">EOAA information: Survey</h3>
        <div className="eoa-configuration-fields">
          <div className="eoa-configuration-field">
            <span className="eoa-configuration-field-label">
              What languages does the survey need to be conducted in?
            </span>
            <div className="eoa-configuration-languages-row">
              <span className="eoa-configuration-maps-to">{ENGLISH_LANGUAGE} and</span>
              <Select
                className="eoa-configuration-alias-period-select"
                variant="standard"
                multiple
                displayEmpty
                value={selectedAdditionalLanguageIds(value.additionalLanguageIds, languages)}
                onChange={(event) => {
                  const selected = event.target.value;
                  onChange({
                    ...value,
                    additionalLanguageIds: typeof selected === 'string' ? selected.split(',') : selected,
                  });
                }}
                renderValue={(selected) => additionalLanguagesLabel(selected, languages)}
                MenuProps={{ PaperProps: { className: 'eoa-configuration-language-menu' } }}
              >
                {additionalSurveyLanguages(languages).map((language) => (
                  <MenuItem key={language.id} value={language.id}>
                    {language.name}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>

          <div className="eoa-configuration-field">
            <span className="eoa-configuration-field-label">
              Please provide the terms for staff roles used across high schools (e.g. teacher, counselor, administrator,
              etc)
            </span>
            <div className="eoa-configuration-removable-list">
              {value.staffRoles.map((role) => (
                <div key={role.id} className="eoa-configuration-removable-row">
                  <TextField
                    className="eoa-configuration-period-name-input"
                    placeholder="Name"
                    value={role.name}
                    onChange={(event) => onChange(updateStaffRole(value, role.id, event.target.value))}
                  />
                  {value.staffRoles.length > 1 && (
                    <IconButton
                      className="eoa-configuration-remove-button"
                      aria-label="Remove staff role"
                      onClick={() => onChange(removeStaffRole(value, role.id))}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="eoa-configuration-add-link"
                onClick={() => onChange(addStaffRole(value))}
              >
                <img src={AddIcon} alt="" width={18} height={18} />
                Add more
              </button>
            </div>
          </div>

          <div className="eoa-configuration-field">
            <span className="eoa-configuration-field-label">
              Please provide an estimate of the number of families, students, and educators who will receive the survey
              per school
            </span>
            <div className="eoa-configuration-estimate-list">
              <div className="eoa-configuration-estimate-row">
                <TextField
                  className="eoa-configuration-narrow-input"
                  placeholder="Number"
                  value={value.estimatedFamilies}
                  error={Boolean(digitsOnlyError(value.estimatedFamilies))}
                  helperText={digitsOnlyError(value.estimatedFamilies)}
                  onChange={(event) => onChange(setDigitField(value, 'estimatedFamilies', event.target.value))}
                  inputProps={{ inputMode: 'numeric' }}
                />
                <span className="eoa-configuration-maps-to">families</span>
              </div>
              <div className="eoa-configuration-estimate-row">
                <TextField
                  className="eoa-configuration-narrow-input"
                  placeholder="Number"
                  value={value.estimatedStudents}
                  error={Boolean(digitsOnlyError(value.estimatedStudents))}
                  helperText={digitsOnlyError(value.estimatedStudents)}
                  onChange={(event) => onChange(setDigitField(value, 'estimatedStudents', event.target.value))}
                  inputProps={{ inputMode: 'numeric' }}
                />
                <span className="eoa-configuration-maps-to">students</span>
              </div>
              <div className="eoa-configuration-estimate-row">
                <TextField
                  className="eoa-configuration-narrow-input"
                  placeholder="Number"
                  value={value.estimatedEducators}
                  error={Boolean(digitsOnlyError(value.estimatedEducators))}
                  helperText={digitsOnlyError(value.estimatedEducators)}
                  onChange={(event) => onChange(setDigitField(value, 'estimatedEducators', event.target.value))}
                  inputProps={{ inputMode: 'numeric' }}
                />
                <span className="eoa-configuration-maps-to">educators</span>
              </div>
            </div>
          </div>
        </div>
      </Panel>
    </>
  );
};

export default EoaaInfoFields;
